import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
	'inline-flex items-center justify-center gap-1.5 rounded-md text-[16px] font-medium transition-colors disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap',
	{
		variants: {
			variant: {
				primary: 'bg-accent text-white border border-accent hover:bg-accent-2 hover:border-accent-2',
				ink: 'bg-ink text-white border border-ink hover:bg-ink-2 hover:border-ink-2',
				ghost: 'bg-transparent text-ink-2 border border-line hover:bg-[#f4f4f3] hover:text-ink hover:border-line-strong',
				outline: 'bg-white text-ink-2 border border-line hover:bg-[#f4f4f3] hover:text-ink',
				danger: 'bg-white text-danger border border-danger hover:bg-danger hover:text-white',
				icon: 'inline-flex items-center justify-center w-7 h-7 rounded-md text-ink-3 hover:bg-[#f4f4f3] hover:text-ink',
				link: 'bg-transparent text-ink-2 hover:text-ink underline-offset-2 hover:underline',
			},
			size: {
				default: 'px-3.5 py-1.5',
				sm: 'px-2.5 py-1 text-[15px]',
				lg: 'px-5 py-2 text-[17px]',
				icon: 'p-0',
			},
		},
		defaultVariants: { variant: 'ghost', size: 'default' },
	}
);

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	({ className, variant, size, asChild = false, ...props }, ref) => {
		const Comp = asChild ? Slot : 'button';
		return <Comp ref={ref} className={cn(buttonVariants({ variant, size, className }))} {...props} />;
	}
);
Button.displayName = 'Button';
